/**
 * RENTAL PRICING ALGORITHM
 * Calculates optimal rental pricing based on market data and property attributes
 *
 * Data Sources:
 * - Rentcast API (primary market data)
 * - HUD Fair Market Rent (baseline floor)
 * - Pricing Knowledge Base (amenity premiums, condition multipliers)
 */

import {
  pricingKnowledgeBase,
  getConditionMultiplier,
  calculateParkingPremium,
  calculateUtilityAdjustment,
  getUtilityLabel
} from '../data/pricing-knowledge-base';
import { getHudFmr, compareToFmr } from '../data/hud-fmr';

/**
 * Main pricing calculation function
 * @param {Object} property - Property details
 * @param {Object} marketData - Market comparison data for the zip code
 * @returns {Object} Pricing recommendation with justifications
 */
function calculateSweetSpot(property, marketData) {
  const kb = pricingKnowledgeBase;
  const adjustments = [];

  // Get HUD FMR for this zip code and bedroom count - this is now the BASE rent
  const hudFmr = getHudFmr(property.bedrooms, property.zipCode);
  const baseRent = hudFmr;  // HUD FMR is the standard baseline

  // Keep market data for reference/comparison
  const marketMedian = marketData.medianRent;

  // 1. CONDITION ADJUSTMENT (applied to HUD base)
  const conditionMultiplier = getConditionMultiplier(property.condition);
  const conditionAdj = baseRent * (conditionMultiplier - 1);

  if (Math.abs(conditionAdj) > 10) {
    const conditionLabel = {
      excellent: 'Excellent condition',
      good: 'Good condition',
      fair: 'Fair condition',
      poor: 'Poor condition'
    };
    adjustments.push({
      factor: conditionLabel[property.condition],
      amount: conditionAdj,
      percentage: ((conditionMultiplier - 1) * 100).toFixed(0)
    });
  }

  // 1.5 UTILITY ADJUSTMENT (when tenant pays utilities)
  const utilityOption = property.utilities || 'tenant-all';
  const utilityAdj = calculateUtilityAdjustment(utilityOption, property.bedrooms);

  if (utilityAdj !== 0) {
    adjustments.push({
      factor: getUtilityLabel(utilityOption),
      amount: utilityAdj,
      category: 'utility'
    });
  }

  // 2. SQUARE FOOTAGE ADJUSTMENT
  const medianSqft = kb.medianSqftByBedroom[property.bedrooms] || 950;
  const sqftDiff = property.sqft - medianSqft;
  const sqftAdj = sqftDiff * kb.sqftAdjustment.perSqftDifference;

  if (Math.abs(sqftAdj) > kb.sqftAdjustment.minimumImpact) {
    adjustments.push({
      factor: sqftDiff > 0
        ? `Larger than typical ${property.bedrooms}BR (${property.sqft} vs ${medianSqft} sqft)`
        : `Smaller than typical ${property.bedrooms}BR (${property.sqft} vs ${medianSqft} sqft)`,
      amount: sqftAdj,
      details: `${Math.abs(sqftDiff)} sqft × $${kb.sqftAdjustment.perSqftDifference}/sqft`
    });
  }

  // 3. BATHROOM PREMIUM/DISCOUNT
  const expectedBaths = kb.expectedBathrooms[property.bedrooms] || 1;
  const bathDiff = property.bathrooms - expectedBaths;

  if (bathDiff > 0) {
    const bathAdj = bathDiff * kb.bathroomAdjustment.extraBathPremium;
    adjustments.push({
      factor: `Extra bathroom (${property.bathrooms} vs typical ${expectedBaths})`,
      amount: bathAdj
    });
  } else if (bathDiff < -0.5) {
    const bathAdj = bathDiff * kb.bathroomAdjustment.missingBathPenalty;
    adjustments.push({
      factor: `Fewer bathrooms (${property.bathrooms} vs typical ${expectedBaths})`,
      amount: bathAdj
    });
  }

  // 4. AMENITY PREMIUMS

  // Parking - now supports multi-select
  const parking = property.amenities.parking;
  if (typeof parking === 'object') {
    // Multi-select parking (new format)
    const parkingPremium = calculateParkingPremium(parking);
    if (parkingPremium > 0) {
      const parkingTypes = [];
      if (parking.garage) parkingTypes.push('garage');
      if (parking.dedicated) parkingTypes.push('dedicated spot');
      if (parking.street) parkingTypes.push('street');
      adjustments.push({
        factor: `Parking: ${parkingTypes.join(' + ')}`,
        amount: parkingPremium,
        category: 'amenity'
      });
    }
  } else {
    // Legacy single-select parking (backwards compatibility)
    const parkingPremiums = kb.amenityPremiums.parking;
    if (parking === 'dedicated') {
      adjustments.push({
        factor: 'Dedicated parking spot',
        amount: parkingPremiums.dedicated,
        category: 'amenity'
      });
    } else if (parking === 'garage') {
      adjustments.push({
        factor: 'Garage parking included',
        amount: parkingPremiums.garage,
        category: 'amenity'
      });
    } else if (parking === 'street') {
      adjustments.push({
        factor: 'Street parking available',
        amount: parkingPremiums.street,
        category: 'amenity'
      });
    }
  }

  // Laundry
  const laundryPremiums = kb.amenityPremiums.laundry;
  if (property.amenities.laundry === 'in-unit') {
    adjustments.push({
      factor: 'In-unit washer/dryer',
      amount: laundryPremiums['in-unit'],
      category: 'amenity'
    });
  } else if (property.amenities.laundry === 'in-building') {
    adjustments.push({
      factor: 'Laundry facilities in building',
      amount: laundryPremiums['in-building'],
      category: 'amenity'
    });
  }

  // Outdoor space
  if (property.amenities.outdoor) {
    const outdoorPremium = property.bedrooms >= 2
      ? kb.amenityPremiums.other.outdoor.largePremium
      : kb.amenityPremiums.other.outdoor.base;
    adjustments.push({
      factor: 'Private outdoor space (patio/balcony/yard)',
      amount: outdoorPremium,
      category: 'amenity'
    });
  }

  // Renovated
  if (property.amenities.updated) {
    adjustments.push({
      factor: 'Recently renovated (kitchen/bath)',
      amount: kb.amenityPremiums.other.renovated,
      category: 'amenity'
    });
  }

  // Pet-friendly
  if (property.amenities.petFriendly) {
    adjustments.push({
      factor: 'Pet-friendly (expands tenant pool)',
      amount: kb.amenityPremiums.other.petFriendly,
      category: 'amenity'
    });
  }

  // Dishwasher
  if (property.amenities.dishwasher) {
    adjustments.push({
      factor: 'Dishwasher included',
      amount: kb.amenityPremiums.other.dishwasher,
      category: 'amenity'
    });
  }

  // HVAC
  const hvacPremiums = kb.amenityPremiums.hvac;
  const hvacLabels = {
    central: 'Central air conditioning (full coverage)',
    hybrid: 'Ductless mini-split + window units provided',
    'split-window-allowed': 'Ductless mini-split + window AC allowed',
    'mini-split': 'Ductless mini-split system',
    'window-provided': 'Window AC units provided (all rooms)',
    'window-optional': 'Window AC available (tenant-installed)'
  };

  if (property.amenities.hvac && hvacPremiums[property.amenities.hvac]) {
    adjustments.push({
      factor: hvacLabels[property.amenities.hvac],
      amount: hvacPremiums[property.amenities.hvac],
      category: 'amenity'
    });
  }

  // Calculate total adjustments
  const totalAdjustment = adjustments.reduce((sum, adj) => sum + adj.amount, 0);

  // Calculate sweet spot: HUD base + all adjustments
  // HUD FMR is the baseline, adjustments scale upward (or down for poor condition)
  const rawSweetSpot = baseRent + totalAdjustment;
  const sweetSpot = Math.round(rawSweetSpot / kb.roundToNearest) * kb.roundToNearest;

  // Calculate pricing range
  const conservativePrice = Math.round(sweetSpot * 0.95 / kb.roundToNearest) * kb.roundToNearest;
  const aggressivePrice = Math.round(sweetSpot * 1.05 / kb.roundToNearest) * kb.roundToNearest;

  // Determine market position (compare to HUD baseline)
  const percentDiff = ((sweetSpot - hudFmr) / hudFmr) * 100;
  let marketPosition;
  if (percentDiff > 10) marketPosition = 'premium pricing';
  else if (percentDiff > 5) marketPosition = 'above baseline';
  else if (percentDiff > 0) marketPosition = 'at baseline with adjustments';
  else if (percentDiff > -5) marketPosition = 'at HUD baseline';
  else marketPosition = 'below baseline';

  // Compare to HUD FMR
  const fmrComparison = compareToFmr(sweetSpot, property.bedrooms, property.zipCode);

  // Generate justification narrative
  const justification = buildJustification(
    property,
    marketData,
    adjustments,
    sweetSpot,
    percentDiff,
    hudFmr,
    marketMedian
  );

  return {
    sweetSpot,
    baseRent: hudFmr,  // HUD FMR is the base
    totalAdjustment,
    adjustments,
    range: {
      conservative: conservativePrice,
      optimal: sweetSpot,
      aggressive: aggressivePrice
    },
    marketPosition,
    percentDiff: percentDiff.toFixed(1),
    justification,
    hudFmr: {
      amount: hudFmr,
      isBase: true,  // HUD is now the base, not just a floor
      comparison: fmrComparison
    },
    marketReference: {
      median: marketMedian,
      neighborhood: marketData.neighborhood
    },
    metadata: {
      zipCode: property.zipCode,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      sqft: property.sqft,
      marketSampleSize: marketData.sampleSize
    }
  };
}

/**
 * Build human-readable justification narrative
 */
function buildJustification(property, marketData, adjustments, sweetSpot, percentDiff, hudFmr, marketMedian) {
  const lines = [];

  // Opening context - HUD is the base
  lines.push(`HUD FMR baseline for ${property.bedrooms}BR: $${hudFmr.toLocaleString()} (FY 2026)`);
  lines.push(`Market reference (${property.zipCode}): $${marketMedian.toLocaleString()}`);

  // Adjustments
  if (adjustments.length > 0) {
    lines.push('');
    lines.push('Adjustments to HUD baseline:');
    adjustments.forEach(adj => {
      const sign = adj.amount >= 0 ? '+' : '';
      lines.push(`  ${sign}$${Math.round(adj.amount)} - ${adj.factor}`);
    });
  }

  // Summary
  lines.push('');
  lines.push(`Recommended sweet spot: $${sweetSpot.toLocaleString()}/month`);

  const diffText = percentDiff >= 0
    ? `${Math.abs(percentDiff).toFixed(1)}% above HUD baseline`
    : `${Math.abs(percentDiff).toFixed(1)}% below HUD baseline`;
  lines.push(`Position: ${diffText}`);

  // Strategy recommendation
  lines.push('');
  if (percentDiff > 10) {
    lines.push('💡 Strategy: Premium positioning. May take 30-45 days to lease. Justified by above-average features.');
  } else if (percentDiff < 0) {
    lines.push('💡 Strategy: Value positioning. Should lease quickly (15-25 days). Competitive for condition.');
  } else {
    lines.push('💡 Strategy: Market-rate positioning. Expected 20-30 days to lease. Balanced approach.');
  }

  return lines;
}

/**
 * Validate property input data
 */
function validateProperty(property) {
  const errors = [];

  if (!property.zipCode || !/^\d{5}$/.test(property.zipCode)) {
    errors.push('Invalid zip code format (must be 5 digits)');
  }

  if (property.bedrooms === undefined || property.bedrooms === null || property.bedrooms < 0 || property.bedrooms > 10) {
    errors.push('Bedrooms must be between 0 (studio) and 10');
  }

  if (!property.bathrooms || property.bathrooms < 0 || property.bathrooms > 10) {
    errors.push('Bathrooms must be between 0 and 10');
  }

  if (!property.sqft || property.sqft < 100 || property.sqft > 10000) {
    errors.push('Square footage must be between 100 and 10,000');
  }

  const validConditions = ['excellent', 'good', 'fair', 'poor'];
  if (!property.condition || !validConditions.includes(property.condition)) {
    errors.push(`Condition must be one of: ${validConditions.join(', ')}`);
  }

  if (!property.amenities || typeof property.amenities !== 'object') {
    errors.push('Amenities must be an object');
  }

  return errors;
}

/**
 * Validate market data
 */
function validateMarketData(marketData) {
  const errors = [];

  if (!marketData.medianRent || marketData.medianRent < 100) {
    errors.push('Market median rent must be at least $100');
  }

  if (marketData.lowRange && marketData.highRange) {
    if (marketData.lowRange >= marketData.highRange) {
      errors.push('Low range must be less than high range');
    }
  }

  return errors;
}

// Export for use in other modules
export { calculateSweetSpot, validateProperty, validateMarketData };
